import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Button, message, Modal, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Banner } from '../../../types/banner';
import { getBannerStatusData } from '../../../utils';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { useDeleteBanner } from '../../../hooks/banners/use-delete-banner';

dayjs.extend(relativeTime);
dayjs.locale('es');

interface GeneralInfoCardProps {
  banner: Banner;
  onDeleted?: () => void;
  onClose: () => void;
}

export const GeneralInfoCard: React.FC<GeneralInfoCardProps> = ({ banner, onDeleted, onClose }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { deleteBanner } = useDeleteBanner();

  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const statusData = getBannerStatusData(banner);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteBanner(banner.bannerId);
      if (res) {
        message.success('Banner eliminado correctamente');
        onDeleted?.();
        onClose();
      } else {
        message.error('No se pudo eliminar el banner');
      }
    } catch (error) {
      message.error('No se pudo eliminar el banner');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <div
        style={{
          background: '#f8fafc',
          borderRadius: 16,
          padding: `${spacing.sm}px ${spacing.md}px`,
          border: '1px solid #e2e8f0'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 0',
              borderBottom: '1px solid #e2e8f0',
              fontSize: fontSize.sm
            }}
          >
            <span style={{ color: '#64748b', fontWeight: 500 }}>Creado</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>
              {banner?.createdAt ? dayjs(banner.createdAt).fromNow() : '-'}
            </span>
          </div>

          {banner?.updatedAt && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: `${spacing.sm}px 0`,
                borderBottom: '1px solid #e2e8f0',
                fontSize: fontSize.sm
              }}
            >
              <span style={{ color: '#64748b', fontWeight: 500 }}>Actualizado</span>
              <span style={{ color: '#0f172a', fontWeight: 600 }}>{dayjs(banner.updatedAt).fromNow()}</span>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${spacing.sm}px 0`,
              borderBottom: '1px solid #e2e8f0',
              fontSize: fontSize.sm
            }}
          >
            <span style={{ color: '#64748b', fontWeight: 500 }}>Estado</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>{statusData.message}</span>
          </div>

          {/* Botón de eliminar */}
          <div
            style={{
              paddingTop: spacing.md,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setShowDeleteModal(true)}
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '8px',
                fontSize: fontSize.base,
                fontWeight: 500
              }}
            >
              Eliminar asset
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de confirmación personalizado */}
      <Modal
        open={showDeleteModal}
        onCancel={() => !isDeleting && setShowDeleteModal(false)}
        footer={null}
        centered
        closable={!isDeleting}
        width={screens.xs ? 320 : screens.sm ? 360 : 400}
        styles={{
          body: { padding: 0 },
          mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          content: { padding: 0 }
        }}
      >
        <div
          style={{
            padding: screens.xs ? spacing.md : spacing.lg,
            textAlign: 'center'
          }}
        >
          {/* Título */}
          <Typography.Title
            level={4}
            style={{
              margin: `0 0 ${spacing.sm}px 0`,
              fontSize: fontSize.xl,
              fontWeight: 700,
              color: '#0f172a'
            }}
          >
            ¿Eliminar este asset?
          </Typography.Title>

          {/* Descripción */}
          <Typography.Text
            style={{
              display: 'block',
              marginBottom: spacing.md,
              fontSize: fontSize.base,
              color: '#64748b',
              lineHeight: 1.6
            }}
          >
            Esta acción es permanente y no se puede deshacer.
          </Typography.Text>

          {/* Botones */}
          <div
            style={{
              display: 'flex',
              flexDirection: screens.xs ? 'column' : 'row',
              gap: spacing.sm,
              justifyContent: 'center'
            }}
          >
            <Button
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
              style={{
                flex: screens.xs ? 'none' : 1,
                width: screens.xs ? '100%' : 'auto',
                height: screens.xs ? spacing.xxl : spacing.xl,
                borderRadius: '8px',
                fontSize: fontSize.base,
                fontWeight: 500
              }}
            >
              Cancelar
            </Button>
            <Button
              danger
              type='primary'
              onClick={handleDelete}
              loading={isDeleting}
              style={{
                flex: screens.xs ? 'none' : 1,
                width: screens.xs ? '100%' : 'auto',
                height: screens.xs ? spacing.xxl : spacing.xl,
                borderRadius: '8px',
                fontSize: fontSize.base,
                fontWeight: 600
              }}
            >
              {isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
